import { TFunction } from "react-i18next";

import { ReportsSharingSource } from "Models/company.config";
import { TaskType } from "Util/enums";

export type ReportsSharingEligibility = {
  anyFinishedTaskType?: boolean;
  requirementKey: string;
  taskTypes: TaskType[];
};

const CHECKLIST_ELIGIBILITY: ReportsSharingEligibility = {
  anyFinishedTaskType: true,
  requirementKey: "opsAppReportsSharingRequirementChecklistAnswersOnTask",
  taskTypes: [],
};

const DEFAULT_REPORT_ELIGIBILITY: ReportsSharingEligibility = {
  anyFinishedTaskType: true,
  requirementKey: "opsAppReportsSharingRequirementFinishedTask",
  taskTypes: [],
};

const TASK_TYPE_TRANSLATION_KEYS: Partial<Record<TaskType, string>> = {
  [TaskType.DELIVERY]: "delivery",
  [TaskType.LOCAL]: "local",
  [TaskType.PACK]: "opsAppReportsSharingTaskTypePack",
  [TaskType.UPLIFT]: "uplift",
  [TaskType.VIDEO_SURVEY]: "videoSurvey",
};

const REPORT_ELIGIBILITY_BY_NAME: Record<string, ReportsSharingEligibility> = {
  "inventory condition report": {
    anyFinishedTaskType: true,
    requirementKey: "opsAppReportsSharingRequirementFinishedTask",
    taskTypes: [],
  },
  "outturn report": {
    requirementKey: "opsAppReportsSharingRequirementFinishedTask",
    taskTypes: [TaskType.DELIVERY, TaskType.LOCAL],
  },
  "mechanical & electrical checklist": {
    anyFinishedTaskType: true,
    requirementKey: "opsAppReportsSharingRequirementMandatoryMeItems",
    taskTypes: [],
  },
  "high risk/high value inventory": {
    requirementKey: "opsAppReportsSharingRequirementHighRiskItems",
    taskTypes: [TaskType.LOCAL, TaskType.PACK, TaskType.UPLIFT, TaskType.DELIVERY],
  },
  "high risk/high value inventory report": {
    requirementKey: "opsAppReportsSharingRequirementHighRiskItems",
    taskTypes: [TaskType.LOCAL, TaskType.PACK, TaskType.UPLIFT, TaskType.DELIVERY],
  },
  "pro-gear inventory": {
    requirementKey: "opsAppReportsSharingRequirementProGearItems",
    taskTypes: [TaskType.PACK, TaskType.UPLIFT, TaskType.DELIVERY, TaskType.LOCAL],
  },
  "property damage report": {
    anyFinishedTaskType: true,
    requirementKey: "opsAppReportsSharingRequirementPropertyDamage",
    taskTypes: [],
  },
  "load & transshipment report": {
    requirementKey: "opsAppReportsSharingRequirementTransshipment",
    taskTypes: [TaskType.UPLIFT],
  },
  "volume determination report": {
    anyFinishedTaskType: true,
    requirementKey: "opsAppReportsSharingRequirementVolumeCalculations",
    taskTypes: [],
  },
  "survey report": {
    requirementKey: "opsAppReportsSharingRequirementVideoSurveyTask",
    taskTypes: [TaskType.VIDEO_SURVEY],
  },
  "vehicle report": {
    requirementKey: "opsAppReportsSharingRequirementMotorcycleItems",
    taskTypes: [TaskType.UPLIFT, TaskType.DELIVERY, TaskType.LOCAL],
  },
  "tv report": {
    anyFinishedTaskType: true,
    requirementKey: "opsAppReportsSharingRequirementTvItems",
    taskTypes: [],
  },
};

const normalizeReportsSharingName = (name?: string) => (name || "").trim().toLowerCase();

export const getReportsSharingEligibility = (
  source: ReportsSharingSource,
  name: string,
): ReportsSharingEligibility => {
  if (source === "checklist") {
    return CHECKLIST_ELIGIBILITY;
  }

  return (
    REPORT_ELIGIBILITY_BY_NAME[normalizeReportsSharingName(name)] ?? DEFAULT_REPORT_ELIGIBILITY
  );
};

export const formatReportsSharingTaskTypes = (
  eligibility: ReportsSharingEligibility,
  translate: TFunction,
): string => {
  if (eligibility.anyFinishedTaskType && eligibility.taskTypes.length === 0) {
    return translate("opsAppReportsSharingAnyFinishedTaskType");
  }

  return eligibility.taskTypes
    .map((taskType) => translate(TASK_TYPE_TRANSLATION_KEYS[taskType] ?? taskType))
    .join(", ");
};
